import { Spin } from 'antd';
import { getISOWeek, getMonth, getYear } from 'date-fns';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TIME_TYPE, UNIT_TYPE } from 'util/config/constant';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { selectGlobalMyProfile } from 'state/global/profile/selector';
import {
  MEASUREMENT_FORMAT_STORAGE_KEY,
  MEASUREMENT_FORMAT
} from 'state/global/profile/util/constant';
import { VisualizationCleanedAreas } from './components';
import {
  fetchCleaningMachineUtilisationMonth,
  fetchCleaningMachineUtilisationWeek,
  fetchCleaningMachineUtilisationYear
} from './state/operation';
import * as selector from './state/selector';

export const CleaningMachineUtilisation = () => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.CLEANING_REPORT);
  const [timeMachineUtilisation, setTimeMachineUtilisation] = useState(
    TIME_TYPE.Year
  );
  const [unitMachineUtilisation, setUnitMachineUtilisation] = useState(
    UNIT_TYPE.hours
  );
  const globalMyProfile = useSelector((state) => selectGlobalMyProfile(state));
  const dispatch = useDispatch();
  const measurementFormat = useMemo(() => {
    return globalMyProfile[MEASUREMENT_FORMAT_STORAGE_KEY];
  }, [globalMyProfile]);

  useEffect(() => {
    const today = new Date();
    const timeFilter = {
      isoYear: null,
      isoMonth: null,
      isoWeek: null
    };
    const year = getYear(today);
    switch (timeMachineUtilisation) {
      case TIME_TYPE.Year: {
        timeFilter.isoYear = year;
        dispatch(fetchCleaningMachineUtilisationYear(timeFilter));
        break;
      }
      case TIME_TYPE.Month: {
        const month = getMonth(today);
        timeFilter.isoMonth = month;
        timeFilter.isoYear = year;
        dispatch(fetchCleaningMachineUtilisationMonth(timeFilter));
        break;
      }
      default: {
        const week = getISOWeek(today);
        timeFilter.isoWeek = week;
        timeFilter.isoYear = year;
        dispatch(fetchCleaningMachineUtilisationWeek(timeFilter));
        break;
      }
    }
  }, [dispatch, timeMachineUtilisation]);

  const { cleaningMachineUtilisationInfo } = useSelector((state) => ({
    cleaningMachineUtilisationInfo:
      selector.selectCleaningMachineUtilisationInfo(state)
  }));

  const totalValue = useMemo(() => {
    return cleaningMachineUtilisationInfo.data.reduce((acc, item) => {
      return (
        acc +
        (unitMachineUtilisation === UNIT_TYPE.hours
          ? item.hours
          : item.cleanedSquareMeter)
      );
    }, 0);
  }, [cleaningMachineUtilisationInfo.data, unitMachineUtilisation]);

  const handleTimeMachineUtilisation = (value) => {
    setTimeMachineUtilisation(value);
  };

  const handleUnitMachineUtilisation = (value) => {
    setUnitMachineUtilisation(value);
  };

  return (
    <>
      <div className="cleaningReports-title">
        {t('machineUtilisationPanel.title')}
      </div>

      <Spin spinning={cleaningMachineUtilisationInfo.isLoading}>
        <div className="cleaningReports-chart cleaningReports-chart_area">
          <div className="cleaningReports-chart_header cleaningReports-chart_area_header">
            <div>
              <span
                className={
                  timeMachineUtilisation === TIME_TYPE.Year
                    ? 'active_title'
                    : ''
                }
                onClick={() => handleTimeMachineUtilisation(TIME_TYPE.Year)}
                aria-hidden="true"
                data-testid="time-year">
                {' '}
                {t('machineUtilisationPanel.thisYear')}{' '}
              </span>{' '}
              |
              <span
                className={
                  timeMachineUtilisation === TIME_TYPE.Month
                    ? 'active_title'
                    : ''
                }
                onClick={() => handleTimeMachineUtilisation(TIME_TYPE.Month)}
                aria-hidden="true"
                data-testid="time-month">
                {' '}
                {t('machineUtilisationPanel.thisMonth')}{' '}
              </span>{' '}
              |
              <span
                className={
                  timeMachineUtilisation === TIME_TYPE.Week
                    ? 'active_title'
                    : ''
                }
                onClick={() => handleTimeMachineUtilisation(TIME_TYPE.Week)}
                aria-hidden="true"
                data-testid="time-week">
                {' '}
                {t('machineUtilisationPanel.thisWeek')}{' '}
              </span>
            </div>
            <div>
              <b>{t('machineUtilisationPanel.showUnit')}: </b>
              <span
                className={
                  unitMachineUtilisation === UNIT_TYPE.cleanedSquareMeter
                    ? 'active_title'
                    : ''
                }
                onClick={() =>
                  handleUnitMachineUtilisation(UNIT_TYPE.cleanedSquareMeter)
                }
                aria-hidden="true"
                data-testid="unit-square">
                {measurementFormat === MEASUREMENT_FORMAT.METRIC
                  ? t('machineUtilisationPanel.squareMeterUnitText')
                  : t('machineUtilisationPanel.squareFeetUnitText')}
              </span>{' '}
              |
              <span
                className={
                  unitMachineUtilisation === UNIT_TYPE.hours
                    ? 'active_title'
                    : ''
                }
                onClick={() => handleUnitMachineUtilisation(UNIT_TYPE.hours)}
                aria-hidden="true"
                data-testid="unit-hour">
                {t('machineUtilisationPanel.timeMeterText')}
              </span>{' '}
            </div>
          </div>

          {!cleaningMachineUtilisationInfo.isLoading &&
            cleaningMachineUtilisationInfo?.data?.map((data) => (
              <VisualizationCleanedAreas
                key={data.name}
                item={data}
                totalValue={totalValue}
                unit={unitMachineUtilisation}
                filterBy={timeMachineUtilisation}
              />
            ))}
        </div>
      </Spin>
    </>
  );
};
